import React, { useEffect, useRef, useState } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { useApp } from '../context/AppContext';
import { sendFissalChat } from '../services/api';
import { triggerHaptic } from '../services/purrSynthesizer';
import {
  getUsbDeviceStatus,
  connectUsbSerial,
  disconnectUsbSerial,
  sendUsbCommand,
  sendAndListenUsbSerial,
  UsbDeviceStatusResult,
  UsbSerialNative,
  isUsbSerialSupported
} from '../services/usbSerial';
import { Terminal as TerminalIcon, Play, Eraser, Cpu, Cable, RefreshCw, Power } from 'lucide-react';

const BAUD_RATES = [9600, 19200, 38400, 57600, 115200, 230400, 921600];

export const WebTerminalView: React.FC = () => {
  const { serverUrl } = useApp();
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermInstance = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);

  const [cmdInput, setCmdInput] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [targetMode, setTargetMode] = useState<'ssh' | 'usb'>('ssh');

  // USB OTG Rig state
  const [usbStatus, setUsbStatus] = useState<UsbDeviceStatusResult | null>(null);
  const [usbBaudRate, setUsbBaudRate] = useState<number>(115200);
  const [usbLoading, setUsbLoading] = useState<boolean>(false);

  const refreshUsb = async () => {
    try {
      const status = await getUsbDeviceStatus();
      setUsbStatus(status);
    } catch {
      setUsbStatus(null);
    }
  };

  useEffect(() => {
    refreshUsb();

    // Listen for USB hardware events if supported
    let statusListener: any;
    let dataListener: any;

    if (isUsbSerialSupported()) {
      UsbSerialNative.addListener('usbStatusChanged', (data: any) => {
        refreshUsb();
        if (xtermInstance.current && targetMode === 'usb') {
          xtermInstance.current.writeln(`\r\n\x1b[33m[USB STATUS] ${data.message || data.status}\x1b[0m`);
        }
      }).then(l => { statusListener = l; });

      UsbSerialNative.addListener('usbDataReceived', (data: any) => {
        if (xtermInstance.current && targetMode === 'usb' && data?.text) {
          xtermInstance.current.write(data.text);
        }
      }).then(l => { dataListener = l; });
    }

    return () => {
      try {
        if (statusListener) statusListener.remove();
        if (dataListener) dataListener.remove();
      } catch {}
    };
  }, [targetMode]);

  useEffect(() => {
    if (!terminalRef.current) return;

    const term = new XTerm({
      cursorBlink: true,
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: 12,
      lineHeight: 1.25,
      theme: {
        background: '#070a0f',
        foreground: '#e2e8f0',
        cursor: '#f59e0b',
        black: '#0b0f17',
        red: '#f87171',
        green: '#4ade80',
        yellow: '#fbbf24',
        blue: '#38bdf8',
        magenta: '#c084fc',
        cyan: '#22d3ee',
        white: '#f8fafc',
      },
      convertEol: true,
      scrollback: 1000,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(terminalRef.current);
    fitAddon.fit();

    printBanner(term, targetMode);

    xtermInstance.current = term;
    fitAddonRef.current = fitAddon;

    const handleResize = () => {
      try { fitAddon.fit(); } catch {}
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      term.dispose();
    };
  }, [targetMode]);

  const printBanner = (term: XTerm, mode: 'ssh' | 'usb') => {
    term.clear();
    if (mode === 'ssh') {
      term.writeln('\x1b[33m╔══════════════════════════════════════════════════════════╗\x1b[0m');
      term.writeln('\x1b[33m║\x1b[0m   \x1b[1;36mFISSAL PRIME QUANTUM TERMINAL INTERFACE v1.2\x1b[0m           \x1b[33m║\x1b[0m');
      term.writeln('\x1b[33m║\x1b[0m   Target: \x1b[32mechodedicated\x1b[0m (/home/echo/workspace)           \x1b[33m║\x1b[0m');
      term.writeln('\x1b[33m║\x1b[0m   Protocol: Direct Agent Relay Link                      \x1b[33m║\x1b[0m');
      term.writeln('\x1b[33m╚══════════════════════════════════════════════════════════╝\x1b[0m');
      term.writeln('');
      term.write('\x1b[32mecho@echodedicated\x1b[0m:\x1b[34m~/workspace\x1b[0m$ ');
    } else {
      term.writeln('\x1b[35m╔══════════════════════════════════════════════════════════╗\x1b[0m');
      term.writeln('\x1b[35m║\x1b[0m   \x1b[1;33mFISSAL USB-C OTG HARDWARE RIG & SERIAL CONSOLE\x1b[0m         \x1b[35m║\x1b[0m');
      term.writeln('\x1b[35m║\x1b[0m   Baud: \x1b[36m' + usbBaudRate + ' 8N1\x1b[0m • Driver: \x1b[32mFTDI/CP2102/CH340/CDC\x1b[0m      \x1b[35m║\x1b[0m');
      term.writeln('\x1b[35m║\x1b[0m   Hot-Unplug Protection: \x1b[32mACTIVE 🛡️\x1b[0m                       \x1b[35m║\x1b[0m');
      term.writeln('\x1b[35m╚══════════════════════════════════════════════════════════╝\x1b[0m');
      term.writeln('');
      term.write('\x1b[35musb@otg-rig\x1b[0m:\x1b[33mserial\x1b[0m$ ');
    }
  };

  const handleToggleTarget = (newMode: 'ssh' | 'usb') => {
    triggerHaptic(20);
    setTargetMode(newMode);
  };

  const handleConnectUsb = async () => {
    triggerHaptic(25);
    setUsbLoading(true);
    try {
      const res = await connectUsbSerial(usbBaudRate);
      await refreshUsb();
      if (xtermInstance.current) {
        if (res.success) {
          xtermInstance.current.writeln(`\r\n\x1b[32m⚡ [USB CONNECTED] Link established at ${usbBaudRate} baud (8N1).\x1b[0m`);
        } else {
          xtermInstance.current.writeln(`\r\n\x1b[31m❌ [USB ERROR] ${res.error || 'Connection failed.'}\x1b[0m`);
        }
        xtermInstance.current.write('\x1b[35musb@otg-rig\x1b[0m:\x1b[33mserial\x1b[0m$ ');
      }
    } finally {
      setUsbLoading(false);
    }
  };

  const handleDisconnectUsb = async () => {
    triggerHaptic(20);
    setUsbLoading(true);
    try {
      await disconnectUsbSerial();
      await refreshUsb();
      if (xtermInstance.current) {
        xtermInstance.current.writeln('\r\n\x1b[33m🔌 [USB DISCONNECTED] Serial port closed.\x1b[0m');
        xtermInstance.current.write('\x1b[35musb@otg-rig\x1b[0m:\x1b[33mserial\x1b[0m$ ');
      }
    } finally {
      setUsbLoading(false);
    }
  };

  const runCommand = async (commandToRun: string) => {
    const term = xtermInstance.current;
    const cmd = commandToRun.trim();
    if (!cmd || !term || isExecuting) return;

    triggerHaptic(20);
    setIsExecuting(true);
    term.writeln(cmd);

    if (cmd === 'clear') {
      term.clear();
      if (targetMode === 'ssh') {
        term.write('\x1b[32mecho@echodedicated\x1b[0m:\x1b[34m~/workspace\x1b[0m$ ');
      } else {
        term.write('\x1b[35musb@otg-rig\x1b[0m:\x1b[33mserial\x1b[0m$ ');
      }
      setIsExecuting(false);
      setCmdInput('');
      return;
    }

    if (targetMode === 'usb') {
      // Execute directly via USB OTG Rig
      try {
        const res = await sendAndListenUsbSerial(cmd, 2500);
        if (res.success && res.response) {
          term.writeln(res.response);
        } else if (!res.success) {
          term.writeln(`\x1b[31m[USB Rig Note] ${res.error || 'No response received'}\x1b[0m`);
        }
      } catch (err: any) {
        term.writeln(`\x1b[31mError: ${err.message}\x1b[0m`);
      } finally {
        term.writeln('');
        term.write('\x1b[35musb@otg-rig\x1b[0m:\x1b[33mserial\x1b[0m$ ');
        setIsExecuting(false);
        setCmdInput('');
        try { fitAddonRef.current?.fit(); } catch {}
      }
    } else {
      // Execute via Homelab Agent / PM2 / SSH Relay
      try {
        const response = await sendFissalChat(`run_command: ${cmd}`, [], 'echo');
        let outputText = response.answer;
        if (response.tools && response.tools.length > 0) {
          outputText = String(response.tools[0].output || response.answer);
        }
        term.writeln('\x1b[37m' + outputText + '\x1b[0m');
      } catch (err: any) {
        term.writeln(`\x1b[31mError: ${err.message}\x1b[0m`);
      } finally {
        term.writeln('');
        term.write('\x1b[32mecho@echodedicated\x1b[0m:\x1b[34m~/workspace\x1b[0m$ ');
        setIsExecuting(false);
        setCmdInput('');
        try { fitAddonRef.current?.fit(); } catch {}
      }
    }
  };

  const handleAuxKey = (key: string) => {
    triggerHaptic(15);
    setCmdInput((prev) => prev + key);
  };

  const quickCommands = targetMode === 'ssh' ? [
    'pm2 status',
    'docker ps',
    'pm2 reload all',
    'cat family_notes.txt',
    'uptime',
    'clear',
  ] : [
    'help',
    'version',
    'status',
    'AT',
    'AT+GMR',
    'reset',
    'clear',
  ];

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-[#070a0f]">
      {/* Terminal Title Bar & Target Mode Selector */}
      <div className="bg-black/60 border-b border-white/10 px-3 py-1.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <TerminalIcon className="w-4 h-4 text-fissal-gold" />
          
          {/* Target Toggle */}
          <div className="flex items-center bg-white/5 p-0.5 rounded-lg border border-white/10">
            <button
              onClick={() => handleToggleTarget('ssh')}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono transition-all ${
                targetMode === 'ssh'
                  ? 'bg-fissal-gold text-slate-950 font-bold'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Cpu className="w-3 h-3" />
              <span>The Relay</span>
            </button>
            <button
              onClick={() => handleToggleTarget('usb')}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono transition-all ${
                targetMode === 'usb'
                  ? 'bg-purple-500 text-white font-bold'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Cable className="w-3 h-3" />
              <span>USB OTG Rig</span>
            </button>
          </div>
        </div>

        {/* Right Action Area */}
        <div className="flex items-center gap-2">
          {targetMode === 'usb' && (
            <div className="flex items-center gap-1.5">
              {/* Baud Rate Selector */}
              <select
                value={usbBaudRate}
                onChange={(e) => setUsbBaudRate(Number(e.target.value))}
                className="bg-black/80 border border-white/10 rounded px-1.5 py-0.5 text-[10px] font-mono text-purple-300 focus:outline-none"
              >
                {BAUD_RATES.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>

              {/* Connect / Disconnect */}
              {usbStatus?.connected ? (
                <button
                  onClick={handleDisconnectUsb}
                  disabled={usbLoading}
                  className="flex items-center gap-1 px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[10px] font-mono hover:bg-rose-500/30 transition-all"
                >
                  <Power className="w-2.5 h-2.5" />
                  <span>Disconnect</span>
                </button>
              ) : (
                <button
                  onClick={handleConnectUsb}
                  disabled={usbLoading}
                  className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px] font-mono hover:bg-emerald-500/30 transition-all"
                >
                  <Power className="w-2.5 h-2.5" />
                  <span>Connect</span>
                </button>
              )}

              <button
                onClick={refreshUsb}
                className="p-1 rounded bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white"
                title="Refresh USB Devices"
              >
                <RefreshCw className="w-3 h-3" />
              </button>
            </div>
          )}

          <button
            onClick={() => runCommand('clear')}
            className="p-1 rounded bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white"
            title="Clear Terminal"
          >
            <Eraser className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* XTerm Screen */}
      <div className="flex-1 p-2 overflow-hidden terminal-container">
        <div ref={terminalRef} className="w-full h-full" />
      </div>

      {/* Auxiliary Mobile Keyboard Bar */}
      <div className="bg-black/80 border-t border-white/10 px-2 py-1 flex items-center gap-1 overflow-x-auto no-scrollbar shrink-0">
        {['|', '~', '/', '-', '_', '$', '&&', '>', '<', 'Tab', 'Esc', '\r\n'].map((sym) => (
          <button
            key={sym}
            onClick={() => handleAuxKey(sym === 'Tab' ? '  ' : sym)}
            className="shrink-0 px-2 py-1 rounded bg-white/10 hover:bg-white/20 active:bg-fissal-gold/30 text-white font-mono text-[11px] min-w-[28px] text-center"
          >
            {sym}
          </button>
        ))}
      </div>

      {/* Quick Command Chips */}
      <div className="bg-[#070a0f] px-2 py-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar shrink-0 border-t border-white/5">
        {quickCommands.map((qc) => (
          <button
            key={qc}
            onClick={() => runCommand(qc)}
            disabled={isExecuting}
            className="shrink-0 px-2 py-0.5 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-white font-mono text-[10px] transition-all disabled:opacity-50"
          >
            {qc}
          </button>
        ))}
      </div>

      {/* Input Bar */}
      <div className="p-2 bg-[#0b0f17] border-t border-white/10 shrink-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            runCommand(cmdInput);
          }}
          className="flex items-center gap-2"
        >
          <span className={`font-mono text-xs font-bold ${targetMode === 'usb' ? 'text-purple-400' : 'text-fissal-gold'}`}>
            {targetMode === 'usb' ? 'serial>' : '$'}
          </span>
          <input
            type="text"
            value={cmdInput}
            onChange={(e) => setCmdInput(e.target.value)}
            placeholder={targetMode === 'usb' ? 'Type serial command (e.g. help, AT)...' : 'Type bash command...'}
            className="flex-1 bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-fissal-gold"
          />
          <button
            type="submit"
            disabled={!cmdInput.trim() || isExecuting}
            className={`p-2 rounded-lg font-bold disabled:opacity-40 transition-all ${
              targetMode === 'usb'
                ? 'bg-purple-500 hover:bg-purple-400 text-white'
                : 'bg-fissal-gold hover:bg-amber-400 text-slate-950'
            }`}
          >
            <Play className="w-4 h-4 fill-current" />
          </button>
        </form>
      </div>
    </div>
  );
};
